import { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { solveTsp, isAutoMode } from '../../../../Services';
import { calculatePathDistance } from '../utils/pathCalculations';

export const usePathSolver = ({ 
  locations, 
  startId, 
  selectedAlgorithm,
  setPath,
  currentModeRef,
  showPathDetails,
  path
}) => {
  const [isSolving, setIsSolving] = useState(false);
  const [solveError, setSolveError] = useState('');
  const [solveResult, setSolveResult] = useState(null);
  const [currentVisiblePath, setCurrentVisiblePath] = useState([]);

  // References for tracking changes
  const prevLocationsRef = useRef(null);
  const prevStartIdRef = useRef(null);

  // Monitor changes in locations and startId, reset path if changed and path exists
  useEffect(() => {
    // Ensure we have previous values for comparison
    if (prevLocationsRef.current !== null && prevStartIdRef.current !== null) {
      // Check if locations changed (length or content)
      const locationsChanged = 
        prevLocationsRef.current.length !== locations.length ||
        JSON.stringify(prevLocationsRef.current) !== JSON.stringify(locations);
      
      // Check if startId changed
      const startIdChanged = prevStartIdRef.current !== startId;
      
      // If changed and current path exists, reset path
      if ((locationsChanged || startIdChanged) && path && path.length > 0) {

        // Ensure we only clear path in auto mode
        if (currentModeRef && isAutoMode(currentModeRef.current)) {
          setPath([]);
          setSolveResult(null);
          setCurrentVisiblePath([]);
        }
      }
    }
    
    // Update reference values for next comparison
    prevLocationsRef.current = [...locations];
    prevStartIdRef.current = startId;
  }, [locations, startId, path, setPath, currentModeRef]);

  // Update solveResult when path or locations change
  useEffect(() => {
    if (path && path.length > 1 && locations && locations.length > 0) {

      // Only update if not showing path details or if we have no visible path yet
      if (!showPathDetails) {
        const distance = calculatePathDistance(path, locations);
        // Estimate time (assuming average speed of 40km/h)
        const avgSpeed = 40; // km/h
        const time = distance / avgSpeed * 60; // Convert to minutes
        
        setSolveResult({
          path: path,
          distance: distance,
          time: time,
          algorithm: selectedAlgorithm
        });
        
        // Also update current visible path to match full path
        setCurrentVisiblePath(path);
      }
    } else {
      setSolveResult(null);
      setCurrentVisiblePath([]);
    }
  }, [path, locations, selectedAlgorithm, showPathDetails]);

  // Auto solve path
  const solvePath = async () => {
    if (locations.length < 2 || !startId) return;
    
    // Reset error and result
    setSolveError('');
    setIsSolving(true);
    setCurrentVisiblePath([]);
    
    try {
      // Generate unique group ID
      const groupId = `tsp_${uuidv4()}`;
      
      // Call TSP solving API with selected algorithm
      const result = await solveTsp(groupId, locations, startId, selectedAlgorithm);
      
      // Update path
      setPath(result.path);
      setCurrentVisiblePath(result.path);

    } catch (error) {
      console.error('TSP solving failed:', error);
      setSolveError(error.message || 'Error solving TSP');
    } finally {
      setIsSolving(false);
    }
  };

  return {
    isSolving,
    solveError,
    solveResult,
    currentVisiblePath,
    setCurrentVisiblePath,
    setSolveResult,
    solvePath
  };
}; 